import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IServicesConfigurations} from "../../../configurations/config-data-sections/services-configurations";
import {IGeoComplyResponse, IGeoLocationStrategyRunParams} from "../geo-location.strategy.interface";
import {GeoComplyFailureReason} from "../geo-comply-error-codes";
import {IGeoComplyHtml5Client, IGeoComplyHtml5ClientFactory} from "./geo-comply-html5-client.interfaces";
import {GEO_TRANSACTION_IDENTIFIER_TYPE} from "../../geo-consts";



export class GeoComplyHtml5Client {

    constructor(private readonly services: IServiceFactoryExtended) {
        this._geoComplyHtml5ClientPromise = this._loadGeoComplyHtml5Client();
    }



    private _geoComplyHtml5ClientPromise: Promise<IGeoComplyHtml5Client>;
    private _onGeolocationResolver: null | ((result: IGeoComplyResponse) => void) = null;
    private get configs(): IServicesConfigurations["GeoLocation"]["Html5"] {
        return this.services.configuration.servicesConfigs.GeoLocation.Html5;
    }

    private async _loadGeoComplyHtml5Lib(): Promise<void> {
        try {
            await this.services.scriptLoader.loadScript({
                url: this.configs.geoLibUrl,
                scriptType: "text/javascript",
                tagId: "geo_comply_html5_lib"
            });
        } catch (err) {
            this.services.logger.error("Failed to load Geo Comply HTML5 library", err);
            throw err;
        }
    }

    private _successHandler = (data: string) => {
        if(this._onGeolocationResolver) {
            this._onGeolocationResolver({
                data: data
            });
        }
        this._onGeolocationResolver = null;
    }

    private _failedHandler = (errorCode: number, errorMessage: string) => {
        if(this._onGeolocationResolver) {
            this._onGeolocationResolver({
                failure: {
                    geoComplyError: {
                        errorCode: errorCode,
                        errorMessage: errorMessage
                    },
                    additionalFailureReason: GeoComplyFailureReason.TechnicalProblem
                }
            });
        }
        this._onGeolocationResolver = null;
    }

    async dispose(): Promise<void> {
        const geoClient = await this._getGeoComplyHtml5Client();

        geoClient.events.off('engine.success', this._successHandler);
        geoClient.events.off('engine.failed', this._failedHandler);
    }

    private async _loadGeoComplyHtml5Client(): Promise<IGeoComplyHtml5Client> {

        await this._loadGeoComplyHtml5Lib();

        const clientFactory = this.services.window.getGlobalProperty('GcHtml5') as IGeoComplyHtml5ClientFactory;
        if(!clientFactory) {
            throw new Error(`Geo Comply HTML5 library didn't create GcHtml5 object on window`);
        }

        const client = clientFactory.createClient();

        client.events
              .on('engine.success', this._successHandler)
              .on('engine.failed', this._failedHandler);
        return client;

    }


    private _getGeoComplyHtml5Client(): Promise<IGeoComplyHtml5Client> {

        if(!this._geoComplyHtml5ClientPromise) {
            this._geoComplyHtml5ClientPromise = this._loadGeoComplyHtml5Client();
        }

        return this._geoComplyHtml5ClientPromise.catch(err => {
            this._geoComplyHtml5ClientPromise = null;
            throw err;
        });
    }


    //gc-html5-ui-modal--visible

    private _bringUserInstructionDialogOnTopTimer: any = null;
    private _bringUserInstructionDialogOnTop(): void {
        this._bringUserInstructionDialogOnTopTimer = setInterval(() => {
            const element = this.services.dom.querySelector('.gc-html5-ui-modal--visible') as HTMLElement;
            if(element) {
                element.style.zIndex = "20000";
            }
        }, 100);
    }

    async runGeoLocation(params: IGeoLocationStrategyRunParams): Promise<IGeoComplyResponse> {
        const geoClient = await this._getGeoComplyHtml5Client();

        geoClient.setLicense(params.licenseWebKey);
        geoClient.setUserId(this.services.user.cid.toString());
        geoClient.customFields.set('identifier_type', GEO_TRANSACTION_IDENTIFIER_TYPE);
        geoClient.customFields.set('session_key', params.locationTransactionId);
        geoClient.setReason(params.reason);
        this._bringUserInstructionDialogOnTop();
        try {
            return await this._executeGeoComplyLocation(geoClient);
        } finally {
            clearInterval(this._bringUserInstructionDialogOnTopTimer);
        }

    }

    private _executeGeoComplyLocation(geoClient: IGeoComplyHtml5Client): Promise<IGeoComplyResponse> {
        return new Promise<IGeoComplyResponse>((resolve) => {

            /**
             * From GeoComply documentation
             * "Timeout: We strongly advise that the Operator App should not have any timeouts that terminate the geolocation process of the SDK. The SDK guarantees that you will receive a callback defined by the JS SDK (e.g. engine.success, *.failed) when the JS SDK finishes geolocation"
             * So this is the reason I don't have a timeout here
             */

            this._onGeolocationResolver = (result: IGeoComplyResponse) => {
                resolve(result);
            };

            //TODO - maybe use requestWithTimeout
            geoClient.request();
        });
    }
}